<!--
  预扣款统计 V2
  展示在扣款开始前生成、成功后不再覆盖的预计扣款冻结快照。
  接口：GET /v1/analytics/order/stats/daily/deduction-forecast-v2
-->
<template>
  <main class="deduction-forecast-page" aria-labelledby="page-title">
    <section class="page-heading">
      <div>
        <h1 id="page-title">预扣款统计V2</h1>
        <p>
          展示扣款开始前生成的冻结快照。成功用户合并 kq + hlb 后按 user_id 去重，
          回溯天数由统计脚本配置，历史快照不会随签约订单状态变化而更新。
        </p>
      </div>
    </section>

    <a-card :bordered="false" class="filter-card">
      <a-form layout="inline" :model="filters" class="filter-form">
        <a-form-item label="目标扣款日">
          <a-range-picker
            v-model:value="dateRange"
            value-format="YYYY-MM-DD"
            :allow-clear="true"
            aria-label="目标扣款日期范围"
          />
        </a-form-item>

        <a-form-item label="签约渠道">
          <a-select
            v-model:value="filters.agreementChannel"
            class="channel-select"
            placeholder="全部渠道"
            allow-clear
            :options="channelOptions"
            aria-label="签约渠道"
          />
        </a-form-item>

        <a-form-item label="签约商户">
          <a-select
            v-model:value="filters.merchantId"
            class="merchant-select"
            placeholder="全部商户"
            allow-clear
            show-search
            option-filter-prop="label"
            :options="merchantOptions"
            aria-label="签约商户"
          />
        </a-form-item>

        <a-form-item class="filter-actions">
          <a-space :size="8" wrap>
            <a-button
              type="primary"
              class="refresh-button"
              :loading="loading"
              @click="handleRefresh"
            >
              刷新
            </a-button>
            <a-button :disabled="loading" @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-alert
      v-if="errorText"
      class="request-alert"
      type="error"
      show-icon
      :message="errorText"
      role="alert"
    >
      <template #action>
        <a-button size="small" danger :loading="loading" @click="handleRefresh">
          重新加载
        </a-button>
      </template>
    </a-alert>

    <section aria-labelledby="summary-title">
      <div class="section-heading">
        <div>
          <h2 id="summary-title">预计扣款汇总</h2>
          <p>汇总口径与当前筛选条件一致，金额单位为元。</p>
        </div>
      </div>

      <div v-if="hasLoaded" class="summary-grid">
        <article
          v-for="item in summaryCards"
          :key="item.key"
          class="summary-card"
          :class="`summary-card--${item.key}`"
          :aria-label="`${item.title}预计扣款汇总`"
        >
          <div class="summary-card__header">
            <span>{{ item.title }}</span>
            <span class="channel-mark">{{ item.mark }}</span>
          </div>
          <div class="summary-card__primary">
            <div>
              <span class="metric-label">预计订单</span>
              <strong>{{ formatCount(item.data.expectedOrderCount) }}</strong>
              <span class="metric-unit">单</span>
            </div>
            <div>
              <span class="metric-label">预计金额</span>
              <strong>{{ formatAmount(item.data.expectedAmount) }}</strong>
              <span class="metric-unit">元</span>
            </div>
          </div>
          <div class="summary-card__details">
            <span>
              首扣 {{ formatCount(item.data.firstOrderCount) }} 单 /
              {{ formatAmount(item.data.firstAmount) }} 元
            </span>
            <span>
              续扣 {{ formatCount(item.data.renewOrderCount) }} 单 /
              {{ formatAmount(item.data.renewAmount) }} 元
            </span>
          </div>
        </article>
      </div>

      <div v-else class="summary-grid" aria-label="汇总数据加载中">
        <a-card v-for="key in ['total', 'kq', 'hlb']" :key="key" :bordered="false">
          <a-skeleton :loading="loading" active :paragraph="{ rows: 2 }" />
          <a-empty
            v-if="!loading"
            :image="simpleEmptyImage"
            description="暂无汇总数据"
          />
        </a-card>
      </div>
    </section>

    <a-card :bordered="false" class="batch-card">
      <template #title>
        <span id="latest-batch-title">最近冻结批次</span>
      </template>
      <template v-if="latestBatch">
        <div class="batch-grid" aria-labelledby="latest-batch-title">
          <div class="batch-item">
            <span>目标扣款日</span>
            <strong>{{ displayValue(latestBatch.targetDate) }}</strong>
          </div>
          <div class="batch-item">
            <span>冻结状态</span>
            <a-tag
              class="contrast-tag"
              :color="statusMeta(latestBatch.status).color"
            >
              {{ statusMeta(latestBatch.status).label }}
            </a-tag>
          </div>
          <div class="batch-item">
            <span>回溯天数</span>
            <strong>{{ formatLookbackDays(latestBatch.lookbackDays) }}</strong>
          </div>
          <div class="batch-item batch-item--wide">
            <span>成功支付来源区间</span>
            <strong>{{ formatSourceRange(latestBatch) }}</strong>
          </div>
          <div class="batch-item batch-item--wide">
            <span>快照生成时间</span>
            <strong>{{ formatDateTime(latestBatch.snapshotAt) }}</strong>
          </div>
        </div>
      </template>
      <a-empty
        v-else-if="!loading"
        :image="simpleEmptyImage"
        description="尚未生成冻结快照"
      />
      <a-skeleton v-else active :paragraph="{ rows: 2 }" />
    </a-card>

    <a-card :bordered="false" class="table-card" title="商户预计扣款明细">
      <template #extra>
        <span class="table-tip">预计订单按签约订单自身 merchant_id 汇总</span>
      </template>

      <a-table
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        :locale="tableLocale"
        :row-key="rowKey"
        :scroll="{ x: 1680 }"
        size="small"
        bordered
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'channel'">
            <a-tag
              class="contrast-tag"
              :color="channelMeta(record.agreementChannel).color"
            >
              {{ record.agreementChannelDisplay || channelMeta(record.agreementChannel).label }}
            </a-tag>
          </template>

          <template v-else-if="column.key === 'merchant'">
            <div class="merchant-cell">
              <strong>{{ record.merchantName || "未配置商户" }}</strong>
              <span>ID：{{ record.merchantId ?? "-" }}</span>
            </div>
          </template>

          <template v-else-if="column.key === 'expectedOrderCount'">
            {{ formatCount(record.expectedOrderCount) }}
          </template>
          <template v-else-if="column.key === 'expectedAmount'">
            {{ formatAmount(record.expectedAmount) }}
          </template>
          <template v-else-if="column.key === 'firstOrderCount'">
            {{ formatCount(record.firstOrderCount) }}
          </template>
          <template v-else-if="column.key === 'firstAmount'">
            {{ formatAmount(record.firstAmount) }}
          </template>
          <template v-else-if="column.key === 'renewOrderCount'">
            {{ formatCount(record.renewOrderCount) }}
          </template>
          <template v-else-if="column.key === 'renewAmount'">
            {{ formatAmount(record.renewAmount) }}
          </template>
          <template v-else-if="column.key === 'lookbackDays'">
            {{ formatLookbackDays(record.lookbackDays) }}
          </template>
          <template v-else-if="column.key === 'sourceRange'">
            {{ formatSourceRange(record) }}
          </template>
          <template v-else-if="column.key === 'snapshotAt'">
            {{ formatDateTime(record.snapshotAt) }}
          </template>
        </template>
      </a-table>
    </a-card>
  </main>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { Empty } from "ant-design-vue";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";

const API_PATH = "/order/stats/daily/deduction-forecast-v2";
const simpleEmptyImage = Empty.PRESENTED_IMAGE_SIMPLE;

const loading = ref(false);
const hasLoaded = ref(false);
const errorText = ref("");
const dateRange = ref([]);
const tableData = ref([]);
const latestBatch = ref(null);
const merchantOptions = ref([]);
let requestSequence = 0;

const filters = reactive({
  agreementChannel: undefined,
  merchantId: undefined,
});

const emptyMetrics = () => ({
  expectedOrderCount: 0,
  expectedAmount: 0,
  firstOrderCount: 0,
  firstAmount: 0,
  renewOrderCount: 0,
  renewAmount: 0,
});

const summary = ref({
  total: emptyMetrics(),
  kq: emptyMetrics(),
  hlb: emptyMetrics(),
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  pageSizeOptions: ["20", "50", "100"],
  showTotal: (total) => `共 ${total.toLocaleString("zh-CN")} 条`,
});

const channelOptions = [
  { label: "快钱", value: "kq" },
  { label: "合利宝", value: "hlb" },
];

const summaryCards = computed(() => [
  {
    key: "total",
    title: "全部渠道",
    mark: "TOTAL",
    data: summary.value.total || emptyMetrics(),
  },
  {
    key: "kq",
    title: "快钱",
    mark: "KQ",
    data: summary.value.kq || emptyMetrics(),
  },
  {
    key: "hlb",
    title: "合利宝",
    mark: "HLB",
    data: summary.value.hlb || emptyMetrics(),
  },
]);

const columns = [
  {
    title: "目标扣款日",
    dataIndex: "targetDate",
    key: "targetDate",
    width: 118,
    fixed: "left",
  },
  {
    title: "签约渠道",
    dataIndex: "agreementChannelDisplay",
    key: "channel",
    width: 96,
    align: "center",
  },
  {
    title: "签约商户",
    key: "merchant",
    width: 220,
  },
  {
    title: "预计订单数",
    dataIndex: "expectedOrderCount",
    key: "expectedOrderCount",
    width: 112,
    align: "right",
  },
  {
    title: "预计金额(元)",
    dataIndex: "expectedAmount",
    key: "expectedAmount",
    width: 130,
    align: "right",
  },
  {
    title: "首扣订单数",
    dataIndex: "firstOrderCount",
    key: "firstOrderCount",
    width: 112,
    align: "right",
  },
  {
    title: "首扣金额(元)",
    dataIndex: "firstAmount",
    key: "firstAmount",
    width: 130,
    align: "right",
  },
  {
    title: "续扣订单数",
    dataIndex: "renewOrderCount",
    key: "renewOrderCount",
    width: 112,
    align: "right",
  },
  {
    title: "续扣金额(元)",
    dataIndex: "renewAmount",
    key: "renewAmount",
    width: 130,
    align: "right",
  },
  {
    title: "回溯天数",
    dataIndex: "lookbackDays",
    key: "lookbackDays",
    width: 100,
    align: "center",
  },
  {
    title: "支付来源区间",
    key: "sourceRange",
    width: 210,
  },
  {
    title: "快照生成时间",
    dataIndex: "snapshotAt",
    key: "snapshotAt",
    width: 180,
  },
];

const tableLocale = computed(() => ({
  emptyText: loading.value
    ? "正在加载冻结快照..."
    : errorText.value
      ? "数据加载失败，请点击重新加载"
      : "暂无符合筛选条件的冻结快照",
}));

const displayValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatCount = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? "-" : number.toLocaleString("zh-CN");
};

// 数据库金额为分，页面统一转换为元并固定两位小数。
const formatAmount = (value) => {
  const number = toFiniteNumber(value);
  if (number === null) return "-";
  return (number / 100).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatLookbackDays = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? "-" : `${formatCount(number)} 天`;
};

const formatSourceRange = (record) => {
  const start = displayValue(record?.sourceStartDate);
  const end = displayValue(record?.sourceEndDate);
  return start === "-" && end === "-" ? "-" : `${start} 至 ${end}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return String(value).replace("T", " ").replace(/Z$/, "");
};

const channelMeta = (channel) => {
  if (channel === "kq") return { label: "快钱", color: "#5b3a96" };
  if (channel === "hlb") return { label: "合利宝", color: "#006b66" };
  return { label: displayValue(channel), color: "#475569" };
};

const statusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["completed", "complete", "success", "frozen"].includes(normalized)) {
    return { label: "已冻结", color: "#176b3a" };
  }
  if (["processing", "running", "pending"].includes(normalized)) {
    return { label: "生成中", color: "#15558d" };
  }
  if (["failed", "error"].includes(normalized)) {
    return { label: "生成失败", color: "#a61b1b" };
  }
  return { label: displayValue(status), color: "#475569" };
};

const rowKey = (record) =>
  record.id ||
  `${record.batchId || "batch"}-${record.agreementChannel || "channel"}-${
    record.merchantId ?? "empty"
  }`;

const normalizeMerchantOptions = (options) =>
  (Array.isArray(options) ? options : [])
    .filter((item) => item.merchantId !== null && item.merchantId !== undefined)
    .map((item) => ({
      label: item.merchantName
        ? `${item.merchantName}（ID: ${item.merchantId}）`
        : `商户 ID: ${item.merchantId}`,
      value: item.merchantId,
    }));

const applyResponse = (data) => {
  tableData.value = Array.isArray(data.data) ? data.data : [];
  pagination.current = Number(data.pageIdx) || 1;
  pagination.pageSize = Number(data.pageSize) || pagination.pageSize;
  pagination.total = Number(data.dataSum) || 0;
  summary.value = {
    total: data.summary?.total || emptyMetrics(),
    kq: data.summary?.kq || emptyMetrics(),
    hlb: data.summary?.hlb || emptyMetrics(),
  };
  latestBatch.value = data.latestBatch || null;
  merchantOptions.value = normalizeMerchantOptions(data.merchantOptions);
  hasLoaded.value = true;
};

const fetchData = async (pageIdx = 1, pageSize = pagination.pageSize) => {
  const currentRequest = ++requestSequence;
  loading.value = true;
  errorText.value = "";

  try {
    const json = await get(analyticsUrl(API_PATH), {
      pageIdx,
      pageSize,
      startDate: dateRange.value?.[0] || "",
      endDate: dateRange.value?.[1] || "",
      agreementChannel: filters.agreementChannel || "",
      merchantId: filters.merchantId,
    });

    if (currentRequest !== requestSequence) return;
    if (json.code !== 200 || !json.data) {
      throw new Error(json.content || "服务返回异常");
    }

    applyResponse(json.data);
  } catch (error) {
    if (currentRequest !== requestSequence) return;
    const reason = error?.message || "网络请求失败";
    errorText.value = hasLoaded.value
      ? `本次刷新失败，当前仍显示上次加载结果。原因：${reason}`
      : `冻结快照加载失败。原因：${reason}`;
  } finally {
    if (currentRequest === requestSequence) loading.value = false;
  }
};

const handleRefresh = () => fetchData(1, pagination.pageSize);

const handleReset = () => {
  dateRange.value = [];
  filters.agreementChannel = undefined;
  filters.merchantId = undefined;
  fetchData(1, pagination.pageSize);
};

const handleTableChange = (page) => {
  fetchData(page.current, page.pageSize);
};

onMounted(() => fetchData());
</script>

<style scoped>
.deduction-forecast-page {
  --forecast-primary: #123c6b;
  --forecast-text: #172033;
  --forecast-muted: #526174;
  --forecast-border: #dce3ed;
  display: grid;
  gap: 16px;
  color: var(--forecast-text);
}

.page-heading {
  padding: 4px 2px;
}

.page-heading h1,
.section-heading h2 {
  margin: 0;
  color: var(--forecast-text);
  font-weight: 650;
}

.page-heading h1 {
  font-size: 24px;
  line-height: 1.4;
}

.page-heading p,
.section-heading p {
  margin: 6px 0 0;
  color: var(--forecast-muted);
  line-height: 1.65;
}

.page-heading p {
  max-width: 960px;
}

.filter-card,
.batch-card,
.table-card,
.summary-card {
  border: 1px solid var(--forecast-border);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgb(23 32 51 / 4%);
}

.filter-form {
  row-gap: 12px;
}

.channel-select {
  width: 140px;
}

.merchant-select {
  width: 260px;
}

.filter-actions {
  margin-inline-end: 0;
}

.refresh-button.ant-btn-primary {
  color: #ffffff;
  background: var(--forecast-primary);
  border-color: var(--forecast-primary);
  font-weight: 600;
}

.refresh-button.ant-btn-primary:hover,
.refresh-button.ant-btn-primary:focus-visible {
  color: #ffffff;
  background: #0d315a;
  border-color: #0d315a;
}

.request-alert {
  border-radius: 8px;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin: 2px 2px 12px;
}

.section-heading h2 {
  font-size: 18px;
  line-height: 1.45;
}

.section-heading p {
  font-size: 13px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.summary-card {
  min-width: 0;
  padding: 18px 20px;
  background: #ffffff;
  border-top: 4px solid #315f8e;
}

.summary-card--kq {
  border-top-color: #6741a5;
}

.summary-card--hlb {
  border-top-color: #087b77;
}

.summary-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--forecast-text);
  font-size: 16px;
  font-weight: 650;
}

.channel-mark {
  padding: 2px 8px;
  color: #40516a;
  background: #edf2f7;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.summary-card__primary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 18px;
}

.summary-card__primary > div {
  min-width: 0;
}

.metric-label {
  display: block;
  margin-bottom: 4px;
  color: var(--forecast-muted);
  font-size: 13px;
}

.summary-card__primary strong {
  color: var(--forecast-text);
  font-size: 23px;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.metric-unit {
  margin-left: 4px;
  color: var(--forecast-muted);
  font-size: 12px;
}

.summary-card__details {
  display: grid;
  gap: 6px;
  margin-top: 16px;
  padding-top: 14px;
  color: #3f4f64;
  border-top: 1px solid #e8edf3;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.55;
}

.batch-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(140px, 1fr));
  gap: 18px 24px;
}

.batch-item {
  display: grid;
  align-content: start;
  gap: 5px;
  min-width: 0;
}

.batch-item--wide {
  grid-column: span 2;
}

.batch-item > span {
  color: var(--forecast-muted);
  font-size: 13px;
}

.batch-item > strong {
  color: var(--forecast-text);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.table-tip {
  color: var(--forecast-muted);
  font-size: 12px;
}

.merchant-cell {
  display: grid;
  gap: 2px;
  line-height: 1.45;
}

.merchant-cell strong {
  color: #25334a;
  font-weight: 600;
  white-space: normal;
}

.merchant-cell span {
  color: #65748a;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.table-card :deep(.ant-table-cell) {
  font-variant-numeric: tabular-nums;
}

.contrast-tag {
  color: #ffffff;
  font-weight: 600;
}

.table-card :deep(.ant-pagination) {
  row-gap: 8px;
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .batch-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }
}

@media (max-width: 768px) {
  .deduction-forecast-page {
    gap: 12px;
  }

  .page-heading h1 {
    font-size: 21px;
  }

  .filter-form,
  .filter-form :deep(.ant-form-item),
  .filter-form :deep(.ant-form-item-control),
  .filter-form :deep(.ant-picker),
  .channel-select,
  .merchant-select {
    width: 100%;
  }

  .filter-form :deep(.ant-form-item) {
    display: block;
    margin-inline-end: 0;
  }

  .filter-actions :deep(.ant-space),
  .filter-actions :deep(.ant-space-item),
  .filter-actions :deep(.ant-btn) {
    width: 100%;
  }

  .deduction-forecast-page :deep(.ant-btn),
  .deduction-forecast-page :deep(.ant-picker),
  .deduction-forecast-page :deep(.ant-select-selector) {
    min-height: 44px;
  }

  .table-card :deep(.ant-pagination-item),
  .table-card :deep(.ant-pagination-prev),
  .table-card :deep(.ant-pagination-next),
  .table-card :deep(.ant-pagination-jump-prev),
  .table-card :deep(.ant-pagination-jump-next) {
    min-width: 44px;
    height: 44px;
    line-height: 42px;
  }

  .table-card :deep(.ant-pagination-prev .ant-pagination-item-link),
  .table-card :deep(.ant-pagination-next .ant-pagination-item-link) {
    min-width: 44px;
    height: 44px;
  }

  .table-card :deep(.ant-pagination-options-quick-jumper input) {
    height: 44px;
  }

  .deduction-forecast-page :deep(.ant-picker),
  .deduction-forecast-page :deep(.ant-select-selector) {
    align-items: center;
  }

  .summary-card {
    padding: 16px;
  }

  .summary-card__primary {
    gap: 12px;
  }

  .summary-card__primary strong {
    font-size: 20px;
  }

  .batch-grid {
    grid-template-columns: 1fr;
  }

  .batch-item--wide {
    grid-column: auto;
  }

  .table-card :deep(.ant-card-head) {
    align-items: flex-start;
    min-height: auto;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .table-card :deep(.ant-card-extra) {
    margin-left: 12px;
    white-space: normal;
  }
}
</style>
